import { useEditor } from "@craftjs/core";
import { useLayer } from "@craftjs/layers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faEye, faEyeSlash } from "@fortawesome/sharp-light-svg-icons";
import { Box, BoxProps, styled } from "@mui/material";
import LayerName from "./components/LayerName";

interface ContainerProps extends BoxProps {
    depth: number;
    selected: boolean;
}

const Container = styled(Box, {
    shouldForwardProp: (prop) => prop !== "depth" && prop !== "selected",
})<ContainerProps>(({ depth, selected, theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    background: selected ? "#05B692" : "transparent",
    color: selected ? "#fff" : "inherit",
    "& svg": {
        fill: selected ? "#fff" : "#808184",
    },
    "& .inner": {
        flex: 1,
        "&> div": {
            padding: 0,
            flex: 1,
            display: "flex",
            marginLeft: theme.spacing(depth + 1),
            alignItems: "center",
        },
    },
}));

const LayerHeader = () => {
    const {
        id,
        depth,
        expanded,
        children,
        connectors: { drag, layerHeader },
        actions: { toggleLayer },
    } = useLayer((layer) => ({
        expanded: layer.expanded,
    }));

    const { hidden, actions, selected } = useEditor((state, query) => {
        const selected = query.getEvent("selected").first() === id;

        return {
            hidden: state.nodes[id] && state.nodes[id].data.hidden,
            selected,
        };
    });

    return (
        <Container ref={drag} depth={depth} selected={selected}>
            <Box
                onClick={() => actions.setHidden(id, !hidden)}
                sx={{
                    height: "100%",
                    cursor: "pointer",
                    ml: 1,
                    mr: 1,
                }}
            >
                <FontAwesomeIcon
                    icon={hidden ? faEyeSlash : faEye}
                    style={{ width: 16, height: 16, opacity: hidden ? 0.5 : 1 }}
                />
            </Box>
            <Box className="inner">
                <Box ref={layerHeader}>
                    <Box className="layer-name" sx={{ display: "flex", flex: 1, mr: 1 }}>
                        <LayerName />
                    </Box>

                    {children && children.length > 0 && (
                        <Box
                            onMouseDown={() => toggleLayer()}
                            sx={{
                                display: "flex",
                                ml: "auto",
                                width: 20,
                                height: "100%",
                                cursor: "pointer",
                            }}
                        >
                            <FontAwesomeIcon
                                icon={expanded ? faChevronUp : faChevronDown}
                                style={{ width: 8, height: 8 }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Container>
    );
};

export default LayerHeader;
